'use client';
/**
 * Wallets — cards with balance, CRUD, archive, transfer between wallets, and
 * manual balance adjustment (audited). Total balance header.
 */
import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { Plus, ArrowLeftRight, Pencil, Archive, SlidersHorizontal, ArrowRight } from 'lucide-react';
import { financeApi, WALLET_TYPE_LABELS, type Wallet, type FxRate } from '@/lib/finance-api';
import { cn, formatUsd, formatMoney } from '@/lib/utils';
import { FinanceShell } from '@/components/finance/FinanceShell';
import { Card, StatCard, Button, Sheet, Field, inputCls, Spinner, EmptyState, Pill } from '@/components/finance/primitives';

const TYPES = Object.keys(WALLET_TYPE_LABELS);

export default function WalletsPage() {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [fxRate, setFxRate] = useState<FxRate | null>(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<Wallet | 'new' | null>(null);
  const [transfer, setTransfer] = useState(false);
  const [adjust, setAdjust] = useState<Wallet | null>(null);

  const load = useCallback(() => {
    Promise.all([financeApi.listWallets(true), financeApi.fxCurrent().catch(() => null)])
      .then(([ws, fx]) => { setWallets(ws); setFxRate(fx); })
      .catch(() => undefined).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = wallets.filter((w) => !w.isArchived);
  // Client-side sums stay per-currency — the VND-converted grand total
  // (via the user's rate) lives on the dashboard.
  const totalVnd = active.filter((w) => w.currency !== 'USD').reduce((a, w) => a + Number(w.balance), 0);
  const totalUsd = active.filter((w) => w.currency === 'USD').reduce((a, w) => a + Number(w.balance), 0);
  const hasUsdWallet = active.some((w) => w.currency === 'USD');
  const rate = fxRate ? Number(fxRate.vndPerUsd) : null;
  // "≈" mirror of a balance in the other currency at the latest user rate
  const eqUsd = (vnd: number | string) => (rate ? formatUsd(Number(vnd) / rate) : null);
  const eqVnd = (usd: number | string) => (rate ? formatMoney(Number(usd) * rate, 'VND') : null);

  return (
    <FinanceShell onQuickAddSuccess={load}>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-text-primary">Ví</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTransfer(true)}><ArrowLeftRight size={15} /> Chuyển</Button>
          <Button onClick={() => setEdit('new')}><Plus size={15} /> Thêm ví</Button>
        </div>
      </div>

      {/* Grand total across both currencies — only meaningful once a USD wallet
          exists AND a rate is set (otherwise USD can't be folded into VND). */}
      {hasUsdWallet && rate && (
        <div className="mb-3">
          <StatCard label="Tổng số dư (quy đổi ₫)" value={totalVnd + totalUsd * rate} accent="savings"
            sub={<span className="text-text-muted">≈ <b className="text-neon-green">{eqUsd(totalVnd + totalUsd * rate)}</b> · tỷ giá {formatMoney(rate, 'VND')}/$</span>} />
        </div>
      )}

      <div className={cn('mb-4 grid gap-3', hasUsdWallet && 'sm:grid-cols-2')}>
        <StatCard label="Tổng số dư ₫ (ví hoạt động)" value={totalVnd}
          sub={rate ? <span className="text-text-muted">≈ <b className="text-neon-cyan">{eqUsd(totalVnd)}</b> · tỷ giá {formatMoney(rate, 'VND')}/$</span> : undefined} />
        {hasUsdWallet && <StatCard label="Tổng số dư $ (ví hoạt động)" value={totalUsd} format={formatUsd}
          sub={rate ? <span className="text-text-muted">≈ <b className="text-neon-green">{eqVnd(totalUsd)}</b></span> : undefined} />}
      </div>

      {loading ? <Spinner /> : wallets.length === 0 ? (
        <EmptyState icon="👛" title="Chưa có ví nào" action={<Button onClick={() => setEdit('new')}><Plus size={15} /> Thêm ví</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {wallets.map((w) => (
            <Card key={w.id} className={cn(w.isArchived && 'opacity-60')}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl text-xl" style={{ background: (w.color || '#8b5cf6') + '22' }}>{w.icon || '👛'}</div>
                  <div>
                    <div className="font-semibold text-text-primary">{w.name}</div>
                    <div className="text-xs text-text-muted">{WALLET_TYPE_LABELS[w.type] ?? w.type}{w.isArchived && ' · đã lưu trữ'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg font-bold tabular-nums text-text-primary">{formatMoney(w.balance, w.currency)}</div>
                  {w.currency === 'USD' && <div className="text-[10px] font-semibold uppercase tracking-wide text-neon-cyan">USD</div>}
                  {rate && (
                    <div className="text-[11px] tabular-nums text-text-muted">≈ {w.currency === 'USD' ? eqVnd(w.balance) : eqUsd(w.balance)}</div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
                <Link href={`/finance/wallets/${w.id}`} className="inline-flex items-center gap-1 rounded-lg bg-[var(--border-color)] px-2 py-1 text-text-secondary hover:text-neon-violet">Lịch sử <ArrowRight size={12} /></Link>
                <button onClick={() => setAdjust(w)} className="inline-flex items-center gap-1 rounded-lg bg-[var(--border-color)] px-2 py-1 text-text-secondary hover:text-neon-cyan"><SlidersHorizontal size={12} /> Điều chỉnh</button>
                <button onClick={() => setEdit(w)} className="inline-flex items-center gap-1 rounded-lg bg-[var(--border-color)] px-2 py-1 text-text-secondary hover:text-neon-violet"><Pencil size={12} /> Sửa</button>
                <button onClick={async () => { await financeApi.updateWallet(w.id, { isArchived: !w.isArchived }); toast.success(w.isArchived ? 'Đã khôi phục' : 'Đã lưu trữ'); load(); }}
                  className="inline-flex items-center gap-1 rounded-lg bg-[var(--border-color)] px-2 py-1 text-text-secondary hover:text-neon-orange"><Archive size={12} /> {w.isArchived ? 'Khôi phục' : 'Lưu trữ'}</button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && <WalletFormSheet wallet={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {transfer && <TransferSheet wallets={wallets.filter((w) => !w.isArchived)} onClose={() => setTransfer(false)} onDone={() => { setTransfer(false); load(); }} />}
      {adjust && <AdjustSheet wallet={adjust} onClose={() => setAdjust(null)} onDone={() => { setAdjust(null); load(); }} />}
    </FinanceShell>
  );
}

function WalletFormSheet({ wallet, onClose, onSaved }: { wallet: Wallet | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(wallet?.name ?? '');
  const [type, setType] = useState(wallet?.type ?? 'CASH');
  const [icon, setIcon] = useState(wallet?.icon ?? '👛');
  const [color, setColor] = useState(wallet?.color ?? '#8b5cf6');
  const [currency, setCurrency] = useState(wallet?.currency ?? 'VND');
  const [balance, setBalance] = useState(wallet ? '' : '0');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Nhập tên ví'); return; }
    setSaving(true);
    try {
      if (wallet) await financeApi.updateWallet(wallet.id, { name, type, icon, color });
      else await financeApi.createWallet({ name, type, icon, color, currency, balance: Number(balance) || 0 } as never);
      toast.success('Đã lưu ví'); onSaved();
    } catch { toast.error('Lưu thất bại'); } finally { setSaving(false); }
  };

  return (
    <Sheet open onClose={onClose} title={wallet ? 'Sửa ví' : 'Thêm ví'}>
      <div className="space-y-3">
        <Field label="Tên ví"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Loại"><select value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>{TYPES.map((t) => <option key={t} value={t}>{WALLET_TYPE_LABELS[t]}</option>)}</select></Field>
          <Field label="Icon (emoji)"><input value={icon} onChange={(e) => setIcon(e.target.value)} className={inputCls} maxLength={4} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Màu"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} className={cn(inputCls, 'h-10 p-1')} /></Field>
          {/* Currency is fixed at creation — changing it later would corrupt the balance semantics */}
          {!wallet ? (
            <Field label="Tiền tệ">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                <option value="VND">₫ VND</option>
                <option value="USD">$ USD</option>
              </select>
            </Field>
          ) : (
            <Field label="Tiền tệ"><input value={wallet.currency === 'USD' ? '$ USD' : '₫ VND'} disabled className={cn(inputCls, 'opacity-60')} /></Field>
          )}
        </div>
        {!wallet && (
          <Field label={`Số dư ban đầu (${currency === 'USD' ? '$' : '₫'})`}>
            <input inputMode="decimal" value={balance} onChange={(e) => setBalance(e.target.value.replace(/[^\d.]/g, ''))} className={inputCls} />
          </Field>
        )}
        <Button onClick={save} disabled={saving} className="w-full">{saving ? 'Đang lưu…' : 'Lưu'}</Button>
      </div>
    </Sheet>
  );
}

function TransferSheet({ wallets, onClose, onDone }: { wallets: Wallet[]; onClose: () => void; onDone: () => void }) {
  const [from, setFrom] = useState(wallets[0]?.id ?? 0);
  const [to, setTo] = useState(wallets[1]?.id ?? wallets[0]?.id ?? 0);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const fromW = wallets.find((w) => w.id === from);
  const toW = wallets.find((w) => w.id === to);
  const crossCurrency = !!fromW && !!toW && fromW.currency !== toW.currency;
  const go = async () => {
    if (from === to) { toast.error('Chọn hai ví khác nhau'); return; }
    if (!Number(amount)) { toast.error('Nhập số tiền'); return; }
    setSaving(true);
    try { await financeApi.transfer({ fromWalletId: from, toWalletId: to, amount: Number(amount) }); toast.success('Đã chuyển'); onDone(); }
    catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Chuyển thất bại');
    } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} title="Chuyển tiền giữa ví">
      <div className="space-y-3">
        <Field label="Từ ví"><select value={from} onChange={(e) => setFrom(Number(e.target.value))} className={inputCls}>{wallets.map((w) => <option key={w.id} value={w.id}>{w.icon} {w.name} — {formatMoney(w.balance, w.currency)}</option>)}</select></Field>
        <Field label="Đến ví"><select value={to} onChange={(e) => setTo(Number(e.target.value))} className={inputCls}>{wallets.map((w) => <option key={w.id} value={w.id}>{w.icon} {w.name}{w.currency === 'USD' ? ' ($)' : ''}</option>)}</select></Field>
        <Field label={`Số tiền (${fromW?.currency === 'USD' ? '$' : '₫'} — theo ví nguồn)`}>
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={inputCls} />
        </Field>
        {crossCurrency && (
          <div className="rounded-xl bg-neon-cyan/10 px-3 py-2 text-xs text-neon-cyan">
            Hai ví khác tiền tệ — số tiền sẽ được quy đổi tự động theo tỷ giá hiện hành (xem mục Tỷ giá).
          </div>
        )}
        <Button onClick={go} disabled={saving} className="w-full">{saving ? 'Đang chuyển…' : 'Chuyển'}</Button>
      </div>
    </Sheet>
  );
}

function AdjustSheet({ wallet, onClose, onDone }: { wallet: Wallet; onClose: () => void; onDone: () => void }) {
  const [target, setTarget] = useState(String(Math.round(Number(wallet.balance))));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try { await financeApi.adjustWallet(wallet.id, { targetBalance: Number(target) || 0, reason: reason || undefined }); toast.success('Đã điều chỉnh'); onDone(); }
    catch { toast.error('Thất bại'); } finally { setSaving(false); }
  };
  return (
    <Sheet open onClose={onClose} title={`Điều chỉnh số dư — ${wallet.name}`}>
      <div className="space-y-3">
        <div className="text-sm text-text-muted">Số dư hiện tại: <b className="text-text-primary">{formatMoney(wallet.balance, wallet.currency)}</b></div>
        <Field label="Số dư mới"><input inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^\d]/g, ''))} className={inputCls} /></Field>
        <Field label="Lý do (ghi vào lịch sử)"><input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="e.g. cash count" /></Field>
        <Button onClick={go} disabled={saving} className="w-full">{saving ? 'Đang lưu…' : 'Cập nhật số dư'}</Button>
      </div>
    </Sheet>
  );
}
